/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Saturday, April 23rd 2022, 10:25:38 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import {
  useAPIFetchItem,
  FetchItemArgs,
  useAPIMutation,
  useAPIRequest,
  UseFetchListArgs,
  APISERVICEPATHS,
  useAPIForm,
  UseAPIFormSpec,
} from "lib/api";
import { Team, TeamValidationSchema } from "lib/schemas";
import { PrimaryKeyValue } from "lib/types";
import { useDeleteOSItem } from "version-control/services";

const teamsAPIServicePath = APISERVICEPATHS.teams;

export const useAPIFetchTeam = (
  teamId: PrimaryKeyValue<Team>,
  fetchOptions: Omit<FetchItemArgs<Team>, "apiServicePath" | "apiPathParams">
) =>
  useAPIFetchItem<Team>({
    apiServicePath: teamsAPIServicePath,
    apiPathParams: [teamId],
    ...fetchOptions,
  });

export const useAPIListTeams = (args: Omit<UseFetchListArgs<Team>, "apiServicePath">) => {
  const [teams, setTeams] = React.useState<Team[]>([]);

  const { makeRequestOnInitialMount, dependencyList = [] } = args;

  const { makeRequest, makeRequestCleanUp, ...otheruseAPIRequestValues } = useAPIRequest<Team[], Team, Team>({
    apiServicePath: teamsAPIServicePath,
    makeRequestOnInitialMount,
  });

  React.useEffect(() => {
    if (makeRequestOnInitialMount) makeRequest({ setValidAPIResponse: setTeams, ...args });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [makeRequest, makeRequestOnInitialMount, ...dependencyList]); // Important dependency for organizations

  return { teams, setTeams, ...otheruseAPIRequestValues };
};

export const useAPIMutateTeam = () =>
  useAPIMutation<Team>({
    apiServicePath: teamsAPIServicePath,
  });

interface UseTeamAPIFormArgs {
  teamId?: Team["id"];
  team?: Team;
  updateSelectedTeam: UseAPIFormSpec<Team>["setAPIFormResponse"];
}

export const useTeamAPIForm = ({ teamId, team, updateSelectedTeam }: UseTeamAPIFormArgs) =>
  useAPIForm<Team>({
    apiServicePath: teamsAPIServicePath,
    apiFormDefaultValues: team,
    primaryKey: "id",
    apiFormDataValidationSchema: TeamValidationSchema,
    apiInitialFetchId: teamId,
    titleKey: "team_name",
    formResetDependencyArray: [team], // This is not a bug my friend, reset the form when a team changes because multiple saves of a team could have the same details. So we reset everytime we get a new instance in memory. Reset needs to be done because of react-hook-form embracing uncontrolled inputs.
    setAPIFormResponse: updateSelectedTeam,
  });

export const useDeleteTeam = () => useDeleteOSItem<Team>(teamsAPIServicePath);
